import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Wrench, Stethoscope, FileClock, Cog, PackageCheck, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { useStore } from "@/components/Layout";
import { StatusBadge } from "@/components/StatusBadge";
import { fmtDate } from "@/lib/status";
import { Card } from "@/components/ui/card";

const KPI = [
  { key: "open", label: "Riparazioni aperte", icon: Wrench, tone: "text-blue-600 bg-blue-50 dark:bg-blue-950/40" },
  { key: "diagnosis", label: "In diagnosi", icon: Stethoscope, tone: "text-sky-600 bg-sky-50 dark:bg-sky-950/40" },
  { key: "quotes_pending", label: "Preventivi in attesa", icon: FileClock, tone: "text-amber-600 bg-amber-50 dark:bg-amber-950/40" },
  { key: "repairing", label: "In lavorazione", icon: Cog, tone: "text-indigo-600 bg-indigo-50 dark:bg-indigo-950/40" },
  { key: "ready", label: "Pronte", icon: PackageCheck, tone: "text-emerald-600 bg-emerald-50 dark:bg-emerald-950/40" },
  { key: "overdue", label: "In ritardo", icon: AlertTriangle, tone: "text-rose-600 bg-rose-50 dark:bg-rose-950/40" },
];

export default function Dashboard() {
  const { selectedStore } = useStore();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard", { params: { store_id: selectedStore } }).then((r) => setData(r.data));
  }, [selectedStore]);

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-heading text-2xl font-extrabold tracking-tight sm:text-3xl">Dashboard</h1>
        <p className="text-sm text-muted-foreground">Panoramica operativa dei negozi</p>
      </div>

      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
        {KPI.map((k) => (
          <Card key={k.key} data-testid={`kpi-${k.key}`} className="p-5">
            <div className={`mb-3 inline-flex h-10 w-10 items-center justify-center rounded-lg ${k.tone}`}>
              <k.icon className="h-5 w-5" />
            </div>
            <div className="font-heading text-3xl font-extrabold">{data?.kpis?.[k.key] ?? "–"}</div>
            <div className="mt-1 text-xs text-muted-foreground">{k.label}</div>
          </Card>
        ))}
      </div>

      <Card className="overflow-hidden">
        <div className="border-b border-border px-5 py-4">
          <h2 className="font-heading text-lg font-bold">Riparazioni recenti</h2>
        </div>
        <div className="divide-y divide-border">
          {(data?.recent || []).map((r) => (
            <button key={r.id} data-testid={`recent-row-${r.repair_number}`}
              onClick={() => navigate(`/repairs/${r.id}`)}
              className="flex w-full items-center gap-4 px-5 py-3 text-left transition-colors hover:bg-secondary">
              <span className="font-mono text-sm font-semibold text-primary">{r.repair_number}</span>
              <span className="flex-1 truncate text-sm">{r.device.brand} {r.device.model} · {r.customer_name}</span>
              <span className="hidden text-xs text-muted-foreground sm:block">{r.store_name}</span>
              <StatusBadge code={r.status_code} />
              <span className="hidden text-xs text-muted-foreground md:block">{fmtDate(r.created_at)}</span>
            </button>
          ))}
          {data && data.recent.length === 0 && (
            <div className="px-5 py-10 text-center text-sm text-muted-foreground">Nessuna riparazione ancora.</div>
          )}
        </div>
      </Card>
    </div>
  );
}
